// src/components/atoms/InteractionOverlay.js

import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { useTheme } from "styled-components";
import PropTypes from "prop-types";

/**
 * InteractionOverlay Component
 *
 * Displays interaction details as an overlay on the video player.
 *
 * Props:
 * - interaction: Object containing interaction details (title, description, media, CTA, etc.).
 * - onClose: Function to call when the overlay is closed.
 */
const InteractionOverlay = ({ interaction, onClose }) => {
  const theme = useTheme();

  return (
    <Box
      position="absolute"
      bottom="20%"
      left="50%"
      transform="translateX(-50%)"
      bgcolor="rgba(0, 0, 0, 0.8)"
      color="white"
      p={2}
      borderRadius="8px"
      display="flex"
      flexDirection="column"
      alignItems="center"
      zIndex={10}
      maxWidth="90%"
      mx="auto"
    >
      <Typography
        variant="h6"
        gutterBottom
        sx={{ fontFamily: "basic-sans", fontWeight: "700" }}
      >
        {interaction.title}
      </Typography>
      <Typography
        variant="body2"
        align="center"
        gutterBottom
        sx={{ fontFamily: "basic-sans" }}
      >
        {interaction.description}
      </Typography>
      {interaction.mediaURL && (
        <Box mb={2}>
          {interaction.mediaType === "image" ? (
            <img
              src={interaction.mediaURL}
              alt={interaction.title}
              style={{
                maxWidth: "300px",
                maxHeight: "150px",
                borderRadius: "4px",
              }}
            />
          ) : (
            <video
              src={interaction.mediaURL}
              controls
              style={{
                maxWidth: "300px",
                maxHeight: "150px",
                borderRadius: "4px",
              }}
            />
          )}
        </Box>
      )}
      {interaction.callToAction && (
        <Box display="flex" flexDirection="column" alignItems="center">
          <Button
            variant="contained"
            fullWidth
            href={interaction.callToActionURL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
            sx={{
              mb: 1,
              backgroundColor: theme.colors.blue,
              color: theme.colors.white,
              borderRadius: "50px",
              padding: "10px 30px",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: theme.colors.green,
                color: theme.colors.white,
                boxShadow: "none",
              },
            }}
          >
            {interaction.callToAction}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={onClose}
            fullWidth
            sx={{
              mb: 1,
              backgroundColor: theme.colors.darkGray,
              color: theme.colors.lightGray,
              borderRadius: "50px",
              padding: "10px 30px",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: theme.colors.lightGray,
                color: theme.colors.darkGray,
                boxShadow: "none",
              },
            }}
          >
            Close
          </Button>
        </Box>
      )}
    </Box>
  );
};

InteractionOverlay.propTypes = {
  interaction: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    mediaURL: PropTypes.string,
    mediaType: PropTypes.oneOf(["image", "video"]),
    callToAction: PropTypes.string,
    callToActionURL: PropTypes.string,
    timestamp: PropTypes.number.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default InteractionOverlay;
